import React from 'react';
import { Badge, Col, Row, Select, Tag } from 'antd';
import { EyeOutlined, StopOutlined, SyncOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import { DataTable } from '../../../../components';
import { JobItem, TenantOption } from '../hooks/useQueueMonitor';

const statusColors: Record<string, string> = {
  queued: 'default',
  running: 'processing',
  completed: 'success',
  failed: 'error',
  cancelled: 'warning',
};

interface JobListProps {
  jobs: JobItem[];
  loading: boolean;
  onRefresh: () => void;
  onViewDetail: (job: JobItem) => void;
  onCancelJob: (jobId: string) => void;
  onRetryJob: (jobId: string) => void;
  tenants: TenantOption[];
  pagination: {
    current: number;
    pageSize: number;
    total: number;
    onChange: (page: number, pageSize: number) => void;
  };
  filters: {
    tenant_id?: string;
    status?: string;
  };
  onFilterChange: (key: 'tenant_id' | 'status', value: string | undefined) => void;
  queueName: string;
}

export const JobList: React.FC<JobListProps> = ({
  jobs,
  loading,
  onRefresh,
  onViewDetail,
  onCancelJob,
  onRetryJob,
  tenants,
  pagination,
  filters,
  onFilterChange,
  queueName,
}) => {
  const columns: ColumnsType<JobItem> = [
    {
      title: 'Job Name',
      dataIndex: 'job_name',
      key: 'job_name',
      width: 220,
    },
    {
      title: 'Tenant',
      dataIndex: 'tenant_name',
      key: 'tenant_name',
      width: 160,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => <Tag color={statusColors[status] || 'default'}>{status.toUpperCase()}</Tag>,
    },
    {
      title: 'Progress',
      dataIndex: 'progress',
      key: 'progress',
      width: 100,
      align: 'right',
      render: (value) => `${value ?? 0}%`,
    },
    {
      title: 'Started At',
      dataIndex: 'started_at',
      key: 'started_at',
      width: 160,
      render: (value) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-'),
    },
    {
      title: '',
      key: 'actions',
      width: 140,
      fixed: 'right',
      render: (_, record) => {
        const actions: Array<{ key: string; label: React.ReactNode; icon: React.ReactNode; danger?: boolean; onClick: () => void }> = [
          {
            key: 'detail',
            label: 'View',
            icon: <EyeOutlined />,
            onClick: () => onViewDetail(record),
          },
        ];

        if (record.status === 'queued' || record.status === 'running') {
          actions.push({
            key: 'cancel',
            label: 'Cancel',
            icon: <StopOutlined />,
            danger: true,
            onClick: () => onCancelJob(record.id),
          });
        }

        if (record.status === 'failed' || record.status === 'cancelled') {
          actions.push({
            key: 'retry',
            label: 'Retry',
            icon: <SyncOutlined />,
            onClick: () => onRetryJob(record.id),
          });
        }

        return (
          <div style={{ display: 'flex', gap: 8 }}>
            {actions.map((action) => (
              <Tag
                key={action.key}
                color={action.danger ? 'error' : 'processing'}
                style={{ cursor: 'pointer', margin: 0 }}
                onClick={action.onClick}
              >
                {action.icon} {action.label}
              </Tag>
            ))}
          </div>
        );
      },
    },
  ];

  return (
    <DataTable
      title={`Jobs in ${queueName}`}
      data={jobs}
      columns={columns}
      loading={loading}
      rowKey="id"
      onRefresh={onRefresh}
      emptyText="No jobs found"
      filters={
        <Row gutter={16}>
          <Col span={8}>
            <Select
              placeholder="Filter by tenant"
              allowClear
              style={{ width: '100%' }}
              value={filters.tenant_id}
              onChange={(value) => onFilterChange('tenant_id', value)}
            >
              {tenants.map((t) => (
                <Select.Option key={t.tenant_id} value={t.tenant_id}>{t.tenant_name}</Select.Option>
              ))}
            </Select>
          </Col>
          <Col span={8}>
            <Select
              placeholder="Filter by status"
              allowClear
              style={{ width: '100%' }}
              value={filters.status}
              onChange={(value) => onFilterChange('status', value)}
            >
              <Select.Option value="queued">Queued</Select.Option>
              <Select.Option value="running">Running</Select.Option>
              <Select.Option value="completed">Completed</Select.Option>
              <Select.Option value="failed">Failed</Select.Option>
              <Select.Option value="cancelled">Cancelled</Select.Option>
            </Select>
          </Col>
          <Col span={8}>
            <Badge status="processing" text={`Total: ${pagination.total}`} />
          </Col>
        </Row>
      }
      pagination={pagination}
    />
  );
};